import { useMemo } from "react";

import { useSessionRoll } from "@/components/attendance/use-session-roll";
import type { ListBox, Read } from "@/components/ui/box.schema";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionWorkspace } from "@/hooks/use-session-workspace";
import { ApiError } from "@/lib/api/client";
import type { TeachingSessionAssignment } from "@/lib/api/sessionPortal";
import type { VisitLevel } from "@/lib/visit/address";
import type { VisitResolution } from "@/lib/visit/registry";

import type { ReviewWorkRecord } from "../review/ReviewWork";
import { useReviewWorkModels } from "../review/use-review-work-models";
import { reviewWork } from "../review/review-plan";
import { REVIEW_VISIT_COPY, sessionName } from "./session-copy";

const copy = REVIEW_VISIT_COPY;
const inactive: ReviewWorkRecord = { id: "inactive-session-review", title: "", points: null, due_at: null, assessment_kind: "scored", external_url: null, rubric: null, status: "draft", grade_item_label: null };
const noop = () => undefined;

/** Several pieces of work: one row each, visiting that piece's own review (A3's `work:<id>/review`), since a List never sits in a row (K3). */
function worksList(sessionId: string, read: Read<TeachingSessionAssignment[]>): ListBox<TeachingSessionAssignment[], TeachingSessionAssignment> {
  const waiting = (work: TeachingSessionAssignment) => work.coverage.awaiting_review_count;
  return {
    kind: "list",
    id: `session-review-${sessionId}`,
    job: copy.job,
    label: copy.label,
    owner: { system: "imam" },
    read,
    fold: "never",
    state: (works) => {
      const count = works.reduce((sum, work) => sum + waiting(work), 0);
      return count ? { class: "waiting", word: copy.toReview(count) } : { class: "quiet", word: copy.nothing };
    },
    detail: () => "",
    rows: (works) => works,
    row: {
      key: (work) => work.id,
      title: (work) => work.title,
      facts: (work) => (waiting(work) ? copy.toReview(waiting(work)) : copy.nothing),
      act: (work) => ({ verb: copy.review, does: "visit", visit: `work:${work.id}/review` }),
    },
  };
}

/**
 * `session:<id>/review` (O10): another session's Review, visited from Follow
 * up under a return line naming the page's session. A session is a workspace,
 * so only its Review box is visited. One piece of work draws its review with
 * A3's `ReviewWork`, judged in place with the roll beside each row; several
 * draw as a List whose rows visit each piece's own review.
 */
export function useSessionReviewVisit(level: VisitLevel | null): VisitResolution | null {
  const { me } = useAuth();
  // A bare `session:<id>` names the whole workspace, which is never visited.
  const permitted = Boolean(level?.job === "review" && me?.workspaces.teaching.available);
  const query = useSessionWorkspace(permitted ? level!.id : undefined);
  const workspace = query.data?.session.id === level?.id ? query.data : undefined;
  const teaching = Boolean(workspace?.viewer.capacities.includes("teaching"));
  const cancelled = workspace?.session.status === "cancelled";
  const works = workspace && teaching && !cancelled ? reviewWork(workspace) : [];
  const single = works.length === 1 ? works[0] : null;
  const roll = useSessionRoll(level?.id ?? "", Boolean(single));
  const marks = useMemo(() => (roll.data ? new Map(roll.data.roster.map((row) => [row.student_id, row.status])) : null), [roll.data]);
  const boxes = useReviewWorkModels({
    work: single ?? inactive,
    timezone: workspace?.course.timezone ?? "UTC",
    termOpen: workspace?.course.term_open ?? false,
    roll: marks,
    ranked: false,
    rank: null,
    quietOpen: true,
    linkKey: 0,
    onState: noop,
    enabled: Boolean(single),
  });
  if (!level) return null;
  // A refusal is the same answer for a session that isn't there and one this person doesn't teach (R1).
  const refusedRead = query.error instanceof ApiError && query.error.status >= 400 && query.error.status < 500;
  if (!permitted || refusedRead || (workspace && !teaching)) return { status: "refused" };
  if (!workspace) {
    if (!query.isError) return { status: "loading" };
    const list = worksList(level.id, { status: "failed", sentence: copy.unread, retry: () => void query.refetch() });
    return { status: "ready", subject: copy.noun, boxes: [list], opens: [list.id] };
  }
  const subject = sessionName(workspace.session);
  if (single) {
    if (boxes.some((box) => box.read.status === "forbidden")) return { status: "refused" };
    return { status: "ready", subject, boxes, opens: [`review-${single.id}`] };
  }
  const list = worksList(level.id, works.length
    ? { status: "ready", value: works, readAt: new Date(query.dataUpdatedAt).toISOString() }
    : { status: "empty", sentence: cancelled ? copy.cancelled : copy.none });
  return { status: "ready", subject, boxes: [list], opens: [list.id] };
}
